import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Headphones, MessageSquareText, Plus, Loader2, Clock, CheckCircle2, Circle, Coins } from "lucide-react";

const StatusBadge = ({ status }) => {
  const { t } = useLang();
  const map = {
    pending: { label: t("status_pending"), cls: "bg-amber-100 text-amber-700", icon: Circle },
    in_progress: { label: t("status_in_progress"), cls: "bg-accent/10 text-accent", icon: Clock },
    resolved: { label: t("status_resolved"), cls: "bg-secondary/20 text-secondary", icon: CheckCircle2 },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2} /> {s.label}
    </span>
  );
};

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ topic: "", message: "", phone: "", student_school: "", preferred_language: lang });
  const [credits, setCredits] = useState(null);

  const load = useCallback(async () => {
    try {
      const [reqs, cr] = await Promise.all([api.get("/rep-requests"), api.get("/credits/me")]);
      setRequests(reqs.data);
      setCredits(cr.data);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/rep-requests", { ...form, preferred_language: lang });
      toast.success(t("request_success"));
      setOpen(false);
      setForm({ topic: "", message: "", phone: "", student_school: "", preferred_language: lang });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="dash-welcome">
              {t("dash_welcome")}, {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="mt-2 text-base text-muted-foreground">{t("dash_subtitle")}</p>
            {credits && credits.school_id && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5" data-testid="credits-badge">
                <Coins className="h-4 w-4 text-primary" strokeWidth={2} />
                <span className="text-sm font-semibold text-foreground" data-testid="credits-balance">{credits.credit_balance}</span>
                <span className="text-sm text-muted-foreground">{t("credits_remaining")} · {credits.school}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button data-testid="dash-ask-ai-btn" asChild variant="outline" className="rounded-full border-2 border-primary font-semibold text-primary hover:bg-primary hover:text-primary-foreground">
              <Link to="/#chat"><MessageSquareText className="mr-1.5 h-4 w-4" /> {t("dash_ask_ai")}</Link>
            </Button>
            <Button data-testid="dash-new-request-btn" onClick={() => setOpen(true)} className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover">
              <Plus className="mr-1.5 h-4 w-4" /> {t("dash_new_request")}
            </Button>
          </div>
        </div>

        {/* Live helper hero card */}
        <div className="mt-8 flex flex-col items-start gap-5 rounded-3xl border border-black/5 bg-accent p-7 text-accent-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/15">
              <Headphones className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="font-heading text-xl font-semibold">{t("helper_title")}</h2>
              <p className="mt-1 max-w-md text-sm text-accent-foreground/85">{t("helper_text")}</p>
            </div>
          </div>
          <Button data-testid="hero-request-btn" onClick={() => setOpen(true)} className="shrink-0 rounded-full bg-white font-semibold text-accent hover:bg-white/90">
            {t("helper_cta")}
          </Button>
        </div>

        {/* Requests list */}
        <div className="mt-10">
          <h2 className="font-heading text-2xl font-semibold text-foreground">{t("dash_requests_title")}</h2>
          {loading ? (
            <div className="mt-6 flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : requests.length === 0 ? (
            <p data-testid="no-requests" className="mt-4 rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
              {t("dash_no_requests")}
            </p>
          ) : (
            <div className="mt-5 space-y-4" data-testid="requests-list">
              {requests.map((r) => (
                <div key={r.id} data-testid={`request-card-${r.id}`} className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground">{r.topic}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{r.message}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  {r.admin_notes && (
                    <div className="mt-4 rounded-2xl bg-surfaceAlt px-4 py-3 text-sm text-foreground">
                      <span className="font-semibold text-accent">Helper: </span>{r.admin_notes}
                    </div>
                  )}
                  <p className="mt-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl sm:max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{t("dash_new_request")}</DialogTitle>
            <DialogDescription>{t("helper_text")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("request_topic")}</label>
              <input
                data-testid="request-topic-input"
                required
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder={t("request_topic_ph")}
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("request_school")}</label>
                <input
                  data-testid="request-school-input"
                  value={form.student_school}
                  onChange={(e) => setForm({ ...form, student_school: e.target.value })}
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("request_phone")}</label>
                <input
                  data-testid="request-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("request_message")}</label>
              <textarea
                data-testid="request-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("request_message_ph")}
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-full">
                {t("request_cancel")}
              </Button>
              <Button data-testid="request-submit-btn" type="submit" disabled={submitting} className="flex-1 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t("request_submit")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
